import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { Paths } from '../../../app/constants/paths';
import { useTenant } from '../../../app/context/tenant-context';
import { useTagsByName } from '../../../app/hooks/folders';
import { useEnsembleModelsQuery } from '../../../app/hooks/tags';
import { useTenants } from '../../../app/hooks/tenants';
import { getEnsembleIdByReference } from '../../../app/services/alerts';
import { Loading } from '../../components/loading/loading';
import { useTabManager } from '../../context/tab-manager-context';
import { usePlantFolderIndex } from '../../hooks/use-folder-tree-index';
import { transformToViewState } from '../../utils/view-state-helpers';
import { AlertType } from '../alertV2/types';

import {
  AssessMode,
  HiddenTag,
  ParentTag,
  PersistView,
  TimeRangesType,
  ViewType,
} from '@controlrooms/models';

dayjs.extend(utc);
dayjs.extend(timezone);

const AlertViewV2: React.FC = () => {
  const navigate = useNavigate();
  const [_search] = useSearchParams();
  const tabRef = _search.get('reference') || '';
  const subsystems = _search.get('subsystems') || '';
  const tagNames = _search.get('tagname') || '';
  const startTime = _search.get('startTime');
  const endTime = _search.get('endTime');
  const { tenant } = useTenant();
  const { currentTenant } = useTenants();
  const { ensembleFamilies } = useEnsembleModelsQuery();

  const { openInNewTab, setViewCollection } = useTabManager();

  // Track if we've already created the tab to prevent infinite loop
  const hasCreatedTabRef = useRef(false);
  const processedRefRef = useRef<string>('');

  const { data: ensembleByReference } = useQuery('ensemble_by_reference', () => {
    if (tabRef) {
      return getEnsembleIdByReference({ reference: tabRef, startTime, endTime });
    }
  });
  const { index: plantIndex } = usePlantFolderIndex();
  const { data: tagsByName } = useTagsByName();

  useEffect(() => {
    const openAlertView = async () => {
      const selectedFolders = ((subsystems as string) || '')?.split(',').map(Number);
      const persistView = {
        view_id: '',
        view: {
          selectedFolders,
          hiddenTags: [] as HiddenTag[],
          pinnedTags: [] as ParentTag[],
          name: 'Alert',
          type: ViewType.ANALYZE,
          showLimits: true,
          timeSelection: {
            timePreset: 'custom',
            timeRange: 'custom',
            startTime,
            endTime,
            timezone: currentTenant.timezone,
          },
          config: {
            [ViewType.ANALYZE]: {
              assessMode:
                ensembleByReference?.result?.alert_type === AlertType.LIMIT
                  ? AssessMode.SYSTEM
                  : AssessMode.MODEL,
              selectedEnsembleId:
                ensembleByReference?.result?.alert_type === AlertType.ANOMALY
                  ? ensembleByReference?.result?.family_id
                  : null,
            },
          },
        },
        ensemble_family_id: '',
      } as PersistView;
      const extractedView = { ...persistView.view };
      const ensembleFamily = ensembleFamilies.find(
        (e) => e.family_id === ensembleByReference?.result?.family_id,
      );
      // TODO: Has to be revisited when we have view alerts
      if (tagsByName) {
        ((tagNames as string) || '')?.split(',').forEach((tag) => {
          const currTag = tagsByName[tag];
          if (currTag) {
            const toPin = new ParentTag(currTag, selectedFolders[0]);
            extractedView.pinnedTags?.push(toPin);
          }
        });
      }
      extractedView.timeSelection.timePreset = 'custom';
      extractedView.timeSelection.timeRange = 'custom' as TimeRangesType;
      extractedView.timeSelection.streamingTimeInSeconds = 0;
      extractedView.timeSelection.startTime = startTime
        ? dayjs.utc(startTime).tz(currentTenant.timezone).toISOString()
        : '';
      extractedView.timeSelection.endTime = endTime
        ? dayjs.utc(endTime).tz(currentTenant.timezone).toISOString()
        : '';
      extractedView.selectedTags = ((tagNames as string) || '')?.split(',');
      extractedView.showLimits = ensembleByReference?.result?.alert_type === 'limit' ? true : false;

      const persistedView = { ...persistView, view: extractedView } as PersistView;

      const adhocView = transformToViewState(persistedView, ensembleFamily || null, tenant, tabRef);

      // Open tab with the ViewState
      await openInNewTab(adhocView);

      // Mark collection as dirty
      setViewCollection((prev) => ({
        ...prev,
        isDirty: true,
      }));

      if (plantIndex) {
        navigate({
          pathname: generatePath(Paths.VIEWS, {
            tenantId: tenant.toString(),
          }),
          search: 'alert=true',
        });
      }
    };

    // Only create tab once per unique reference
    if (tabRef && plantIndex && tagsByName && ensembleByReference) {
      // Skip if we already processed this reference
      if (hasCreatedTabRef.current && processedRefRef.current === tabRef) {
        return;
      }
      hasCreatedTabRef.current = true;
      processedRefRef.current = tabRef;
      openAlertView();
    }
  }, [
    subsystems,
    startTime,
    endTime,
    currentTenant.timezone,
    ensembleByReference?.result?.alert_type,
    ensembleByReference?.result?.family_id,
    ensembleFamilies,
    tagsByName,
    tagNames,
    tenant,
    tabRef,
    openInNewTab,
    setViewCollection,
    plantIndex,
    navigate,
    ensembleByReference,
  ]);

  return <Loading />;
};

export default AlertViewV2;
